/* eslint-disable local-rules/no-exported-domain-types-outside-domain */
"use client";

/**
 * TweaksProvider — the global, dev-only tweaks v2 provider (requirement #4).
 *
 * Mounted once at the app root. In production builds it renders children and
 * NOTHING else — no dev code, fetches, or store reads execute; every
 * `useTweaks` boolean is false, so each consuming component's fall-through
 * branch (the existing rendering) runs. That existing option is exactly what
 * a production leak would trivially invoke.
 *
 * In dev it:
 *  - collects `useTweaks` registrations and syncs them (debounced, unioned
 *    per context) through the dev-server store seam, adopting the returned
 *    context files as the hook's boolean source;
 *  - re-reads registered contexts on window focus and on `context-updated`
 *    beacon messages, so page/agent edits to the JSON reach the render;
 *  - carries the screen-side half of the beacon protocol: broadcasts
 *    focus-aware presence (which contexts/marks this tab renders, with each
 *    mark's registration-carried reveal descriptor — census F08) and answers
 *    `locate` messages by running the mark's registered `onReveal` callback
 *    when one exists, then scrolling to and flashing the mark's registered
 *    element. See `beacon.ts` for the full message contract.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ReactElement, ReactNode } from "react";
import { TweaksBeacon, TweaksBeaconMessageType } from "./beacon";
import type {
  TweaksBeaconMessage,
  TweaksBeaconPresenceContext,
  TweaksBeaconPresenceMark,
  TweaksBeaconRevealDescriptor,
} from "./beacon";
import { fetchContextFile, registerContextMarks } from "./client";
import { registrationEntryLeafNames, registrationLeafNames } from "./types";
import type {
  TweakContextFile,
  TweakMarkReveal,
  TweaksRegistrationMark,
  TweaksRevealSpec,
} from "./types";

const REGISTRATION_SYNC_DEBOUNCE_MS = 80;
/**
 * How often a VISIBLE tab re-reads its registered contexts.
 *
 * The beacon covers browser-to-browser edits instantly, but it cannot cover the
 * other half of how this store is written: the CLI and the handler edit the
 * JSON on disk, and a file write has no tab to broadcast from. Focus used to be
 * the only thing that closed that gap, which made a pick look like it had not
 * applied until you clicked the window — the exact failure this poll removes.
 *
 * Gated on visibility rather than focus: an unfocused window that is still on
 * screen is still being looked at, and a poll that stopped on blur would miss
 * exactly the side-by-side arrangement this tool is used in. A HIDDEN tab does
 * stop polling — nobody can see it — and catches up through the
 * visibilitychange handler the moment it comes back, which does not depend on
 * `focus` also firing.
 */
const CONTEXT_POLL_INTERVAL_MS = 1200;
const PRESENCE_DEBOUNCE_MS = 120;
const LOCATE_FLASH_DURATION_MS = 1800;
const LOCATE_FLASH_SHADOW =
  "0 0 0 3px rgba(102, 254, 193, 0.95), 0 0 28px 8px rgba(102, 254, 193, 0.45)";
const LOCATE_FLASH_SHADOW_OFF =
  "0 0 0 3px rgba(102, 254, 193, 0), 0 0 28px 8px rgba(102, 254, 193, 0)";

interface TweaksRegistrationRecord {
  context: string;
  marks: TweaksRegistrationMark[];
}

interface TweaksRevealRecord {
  context: string;
  reveals: TweaksRevealSpec;
}

export interface TweaksProviderValue {
  /** Context files adopted from the store — the hook's boolean source. */
  files: Readonly<Record<string, TweakContextFile>>;
  register: (args: {
    instanceId: string;
    context: string;
    marks: TweaksRegistrationMark[];
  }) => void;
  unregister: (args: { instanceId: string }) => void;
  setMarkElement: (args: {
    instanceId: string;
    context: string;
    mark: string;
    element: HTMLElement | null;
  }) => void;
  /** Latest per-mark reveal metadata for one hook instance (census F08).
   *  Called every render (callbacks are live closures); presence rebroadcasts
   *  only when the serializable descriptor content changes. */
  setMarkReveals: (args: {
    instanceId: string;
    context: string;
    reveals: TweaksRevealSpec;
  }) => void;
}

const TweaksReactContext = createContext<TweaksProviderValue | null>(null);

/** Null whenever the dev provider is absent or disabled — `useTweaks` then
 *  returns all-false and touches nothing. */
export function useTweaksProviderValue(): TweaksProviderValue | null {
  return useContext(TweaksReactContext);
}

function markElementKey({
  context,
  mark,
}: {
  context: string;
  mark: string;
}): string {
  return `${context} ${mark}`;
}

function createTabId(): string {
  return typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `tab-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

const LOCATE_SCROLL_OPTIONS: ScrollIntoViewOptions = {
  behavior: "smooth",
  block: "center",
  inline: "nearest",
};

/** Scroll the mark's element into view and pulse a mint halo around it. */
function flashElement(element: HTMLElement): void {
  element.scrollIntoView(LOCATE_SCROLL_OPTIONS);
  element.animate(
    [
      { boxShadow: LOCATE_FLASH_SHADOW_OFF },
      { boxShadow: LOCATE_FLASH_SHADOW },
      { boxShadow: LOCATE_FLASH_SHADOW_OFF },
      { boxShadow: LOCATE_FLASH_SHADOW },
      { boxShadow: LOCATE_FLASH_SHADOW_OFF },
    ],
    { duration: LOCATE_FLASH_DURATION_MS, easing: "ease-in-out" },
  );
}

/** Union all live registrations of one context: marks merged by name; a mark
 *  registered by several component instances contributes each entry whose
 *  leaf options are all new (first registration wins the base order). */
function computeUnionMarks({
  context,
  registrations,
}: {
  context: string;
  registrations: ReadonlyMap<string, TweaksRegistrationRecord>;
}): TweaksRegistrationMark[] {
  const mergedByName = new Map<string, TweaksRegistrationMark>();
  registrations.forEach((record) => {
    if (record.context !== context) {
      return;
    }
    record.marks.forEach((mark) => {
      const existing = mergedByName.get(mark.name);
      if (!existing) {
        mergedByName.set(mark.name, {
          name: mark.name,
          entries: [...mark.entries],
        });
        return;
      }
      const knownLeaves = new Set(registrationLeafNames(existing));
      mark.entries.forEach((entry) => {
        const entryLeaves = registrationEntryLeafNames([entry]);
        if (entryLeaves.every((leafName) => !knownLeaves.has(leafName))) {
          existing.entries.push(entry);
          entryLeaves.forEach((leafName) => knownLeaves.add(leafName));
        }
      });
    });
  });
  return [...mergedByName.values()];
}

function TweaksDevProvider({
  children,
}: {
  children: ReactNode;
}): ReactElement {
  const [files, setFiles] = useState<Record<string, TweakContextFile>>({});
  const [tabId] = useState(createTabId);

  const registrationsRef = useRef(
    new Map<string, TweaksRegistrationRecord>(),
  );
  /** instanceId → latest reveal metadata (live callbacks — census F08). */
  const revealsRef = useRef(new Map<string, TweaksRevealRecord>());
  /** Serializable fingerprint of the last-broadcast reveal descriptors. */
  const revealFingerprintRef = useRef("");
  /** markElementKey → instanceId → element. */
  const elementsRef = useRef(new Map<string, Map<string, HTMLElement>>());
  const pendingSyncContextsRef = useRef(new Set<string>());
  const syncTimerRef = useRef<number | null>(null);
  const presenceTimerRef = useRef<number | null>(null);
  /** Last-synced registration payload per context — skips no-op re-posts. */
  const lastSyncedPayloadRef = useRef(new Map<string, string>());
  const beaconRef = useRef<TweaksBeacon | null>(null);

  const adoptFile = useCallback((file: TweakContextFile): void => {
    setFiles((previous) => {
      const existing = previous[file.context];
      return existing && existing.updatedAt === file.updatedAt
        ? previous
        : { ...previous, [file.context]: file };
    });
  }, []);

  /** The registered reveal for one context/mark — first registering instance
   *  wins (a mark rarely has two hosts; when it does they agree). */
  const findMarkReveal = useCallback(
    ({
      context,
      mark,
    }: {
      context: string;
      mark: string;
    }): TweakMarkReveal | null => {
      for (const record of revealsRef.current.values()) {
        if (record.context !== context) {
          continue;
        }
        const reveal = record.reveals[mark];
        if (reveal) {
          return reveal;
        }
      }
      return null;
    },
    [],
  );

  const broadcastPresence = useCallback((): void => {
    const contextsByName = new Map<string, Set<string>>();
    registrationsRef.current.forEach((record) => {
      const markNames =
        contextsByName.get(record.context) ?? new Set<string>();
      record.marks.forEach((mark) => markNames.add(mark.name));
      contextsByName.set(record.context, markNames);
    });
    const describeMark = (
      context: string,
      mark: string,
    ): TweaksBeaconPresenceMark => {
      const reveal = findMarkReveal({ context, mark });
      const descriptor: TweaksBeaconRevealDescriptor | null = reveal
        ? {
            label: reveal.label,
            hint: reveal.hint ?? null,
            available: typeof reveal.onReveal === "function",
          }
        : null;
      return { mark, reveal: descriptor };
    };
    const contexts: TweaksBeaconPresenceContext[] = [
      ...contextsByName.entries(),
    ].map(([context, markNames]) => ({
      context,
      marks: [...markNames].map((mark) => describeMark(context, mark)),
    }));
    beaconRef.current?.publish({
      type: TweaksBeaconMessageType.Presence,
      tabId,
      focused:
        typeof document !== "undefined" &&
        document.hasFocus() &&
        !document.hidden,
      contexts,
      at: Date.now(),
    });
  }, [findMarkReveal, tabId]);

  const schedulePresence = useCallback((): void => {
    if (presenceTimerRef.current != null) {
      window.clearTimeout(presenceTimerRef.current);
    }
    presenceTimerRef.current = window.setTimeout(() => {
      presenceTimerRef.current = null;
      broadcastPresence();
    }, PRESENCE_DEBOUNCE_MS);
  }, [broadcastPresence]);

  const flushSyncs = useCallback((): void => {
    const contexts = [...pendingSyncContextsRef.current];
    pendingSyncContextsRef.current.clear();
    contexts.forEach((context) => {
      const marks = computeUnionMarks({
        context,
        registrations: registrationsRef.current,
      });
      if (marks.length === 0) {
        return;
      }
      const payloadKey = JSON.stringify(marks);
      if (lastSyncedPayloadRef.current.get(context) === payloadKey) {
        return;
      }
      lastSyncedPayloadRef.current.set(context, payloadKey);
      void registerContextMarks({ context, marks })
        .then((file) => {
          adoptFile(file);
          beaconRef.current?.publish({
            type: TweaksBeaconMessageType.ContextUpdated,
            context,
            at: Date.now(),
          });
        })
        .catch(() => {
          // Allow a retry on the next registration change.
          lastSyncedPayloadRef.current.delete(context);
        });
    });
  }, [adoptFile]);

  const scheduleSync = useCallback(
    (context: string): void => {
      pendingSyncContextsRef.current.add(context);
      if (syncTimerRef.current != null) {
        window.clearTimeout(syncTimerRef.current);
      }
      syncTimerRef.current = window.setTimeout(() => {
        syncTimerRef.current = null;
        flushSyncs();
      }, REGISTRATION_SYNC_DEBOUNCE_MS);
    },
    [flushSyncs],
  );

  const register = useCallback(
    (args: {
      instanceId: string;
      context: string;
      marks: TweaksRegistrationMark[];
    }): void => {
      registrationsRef.current.set(args.instanceId, {
        context: args.context,
        marks: args.marks,
      });
      scheduleSync(args.context);
      schedulePresence();
    },
    [scheduleSync, schedulePresence],
  );

  const unregister = useCallback(
    (args: { instanceId: string }): void => {
      // Unmounting is NOT removal-from-code: no server sync happens here, so
      // nothing gets orphan-flagged by a page simply navigating away.
      registrationsRef.current.delete(args.instanceId);
      revealsRef.current.delete(args.instanceId);
      elementsRef.current.forEach((elementsByInstance) =>
        elementsByInstance.delete(args.instanceId),
      );
      schedulePresence();
    },
    [schedulePresence],
  );

  /** Serializable content of the reveal registry — the rebroadcast trigger. */
  const computeRevealFingerprint = useCallback((): string => {
    const rows: [string, string, string, string | null, boolean][] = [];
    revealsRef.current.forEach((record, instanceId) => {
      Object.entries(record.reveals).forEach(([mark, reveal]) => {
        rows.push([
          instanceId,
          record.context,
          `${mark} ${reveal.label}`,
          reveal.hint ?? null,
          typeof reveal.onReveal === "function",
        ]);
      });
    });
    rows.sort((first, second) =>
      `${first[0]} ${first[1]} ${first[2]}`.localeCompare(
        `${second[0]} ${second[1]} ${second[2]}`,
      ),
    );
    return JSON.stringify(rows);
  }, []);

  const setMarkReveals = useCallback(
    (args: {
      instanceId: string;
      context: string;
      reveals: TweaksRevealSpec;
    }): void => {
      if (Object.keys(args.reveals).length === 0) {
        revealsRef.current.delete(args.instanceId);
      } else {
        revealsRef.current.set(args.instanceId, {
          context: args.context,
          reveals: args.reveals,
        });
      }
      const fingerprint = computeRevealFingerprint();
      if (fingerprint !== revealFingerprintRef.current) {
        revealFingerprintRef.current = fingerprint;
        schedulePresence();
      }
    },
    [computeRevealFingerprint, schedulePresence],
  );

  const setMarkElement = useCallback(
    (args: {
      instanceId: string;
      context: string;
      mark: string;
      element: HTMLElement | null;
    }): void => {
      const key = markElementKey({ context: args.context, mark: args.mark });
      const elementsByInstance =
        elementsRef.current.get(key) ?? new Map<string, HTMLElement>();
      if (args.element) {
        elementsByInstance.set(args.instanceId, args.element);
      } else {
        elementsByInstance.delete(args.instanceId);
      }
      elementsRef.current.set(key, elementsByInstance);
    },
    [],
  );

  const refetchContext = useCallback(
    (context: string): void => {
      void fetchContextFile(context)
        .then((file) => {
          if (file) {
            adoptFile(file);
          }
        })
        .catch(() => undefined);
    },
    [adoptFile],
  );

  const refetchRegisteredContexts = useCallback((): void => {
    const contexts = new Set<string>();
    registrationsRef.current.forEach((record) =>
      contexts.add(record.context),
    );
    contexts.forEach((context) => refetchContext(context));
  }, [refetchContext]);

  // The poll. `adoptFile` compares `updatedAt`, so an unchanged read costs one
  // small localhost GET and causes no render at all.
  useEffect(() => {
    let timer: number | null = null;
    const tick = (): void => {
      if (!document.hidden && registrationsRef.current.size > 0) {
        refetchRegisteredContexts();
      }
      timer = window.setTimeout(tick, CONTEXT_POLL_INTERVAL_MS);
    };
    timer = window.setTimeout(tick, CONTEXT_POLL_INTERVAL_MS);
    return () => {
      if (timer != null) window.clearTimeout(timer);
    };
  }, [refetchRegisteredContexts]);

  const handleBeaconMessage = useCallback(
    (message: TweaksBeaconMessage): void => {
      if (message.type === TweaksBeaconMessageType.PresenceRequest) {
        broadcastPresence();
        return;
      }
      if (message.type === TweaksBeaconMessageType.Locate) {
        if (message.tabId && message.tabId !== tabId) {
          return;
        }
        // Host-owned reveal first (census F08): put the page into the state
        // where the mark's options differ, then flash the mark's element.
        const reveal = findMarkReveal({
          context: message.context,
          mark: message.mark,
        });
        if (reveal?.onReveal) {
          try {
            reveal.onReveal();
          } catch (error) {
            console.error(
              `[tweaks2] onReveal for "${message.context} / ${message.mark}" threw:`,
              error,
            );
          }
        }
        const elementsByInstance = elementsRef.current.get(
          markElementKey({ context: message.context, mark: message.mark }),
        );
        const [element] = elementsByInstance
          ? [...elementsByInstance.values()]
          : [];
        if (element) {
          flashElement(element);
        }
        return;
      }
      if (message.type === TweaksBeaconMessageType.ContextUpdated) {
        const isRegistered = [...registrationsRef.current.values()].some(
          (record) => record.context === message.context,
        );
        if (isRegistered) {
          refetchContext(message.context);
        }
      }
    },
    [broadcastPresence, findMarkReveal, refetchContext, tabId],
  );
  const handleBeaconMessageRef = useRef(handleBeaconMessage);
  useEffect(() => {
    handleBeaconMessageRef.current = handleBeaconMessage;
  }, [handleBeaconMessage]);

  useEffect(() => {
    const beacon = new TweaksBeacon();
    beaconRef.current = beacon;
    const unsubscribe = beacon.subscribe((message) =>
      handleBeaconMessageRef.current(message),
    );

    const handleFocus = (): void => {
      broadcastPresence();
      refetchRegisteredContexts();
    };
    const handleBlurOrVisibility = (): void => {
      broadcastPresence();
      // Coming BACK to a backgrounded tab must re-read, not just re-announce.
      // The poll deliberately skips a hidden tab (nobody is looking at it), so
      // this is that tab's catch-up — and it does not depend on `focus` also
      // firing, which varies by browser and by whether the switch was between
      // tabs or between windows.
      if (!document.hidden) {
        refetchRegisteredContexts();
      }
    };
    const handlePageHide = (): void => {
      beacon.publish({
        type: TweaksBeaconMessageType.Presence,
        tabId,
        focused: false,
        contexts: [],
        at: Date.now(),
      });
    };
    window.addEventListener("focus", handleFocus);
    window.addEventListener("blur", handleBlurOrVisibility);
    document.addEventListener("visibilitychange", handleBlurOrVisibility);
    window.addEventListener("pagehide", handlePageHide);
    broadcastPresence();
    refetchRegisteredContexts();

    return () => {
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("blur", handleBlurOrVisibility);
      document.removeEventListener("visibilitychange", handleBlurOrVisibility);
      window.removeEventListener("pagehide", handlePageHide);
      if (syncTimerRef.current != null) {
        window.clearTimeout(syncTimerRef.current);
      }
      if (presenceTimerRef.current != null) {
        window.clearTimeout(presenceTimerRef.current);
      }
      unsubscribe();
      beacon.close();
      beaconRef.current = null;
    };
  }, [broadcastPresence, refetchRegisteredContexts, tabId]);

  const value = useMemo<TweaksProviderValue>(
    () => ({ files, register, unregister, setMarkElement, setMarkReveals }),
    [files, register, unregister, setMarkElement, setMarkReveals],
  );

  return (
    <TweaksReactContext.Provider value={value}>
      {children}
    </TweaksReactContext.Provider>
  );
}

/** The globally-wrapped provider. Disabled outside dev: children render bare
 *  and the whole dev machinery is dead code the bundler strips. */
export function TweaksProvider({
  children,
}: {
  children: ReactNode;
}): ReactElement {
  if (process.env.NODE_ENV === "production") {
    return <>{children}</>;
  }
  return <TweaksDevProvider>{children}</TweaksDevProvider>;
}
